import { useState, useCallback, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  ReactFlow,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  MarkerType,
  ReactFlowProvider,
} from "reactflow";
import "reactflow/dist/style.css";
import { Sparkles, RefreshCw, Check, Zap, Target, Cog, Users, Palette, Heart } from "lucide-react";
// animations removed
import { toast } from "sonner";
// PDF export removed per user request

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Custom node component with likes
const IdeaNode = ({ data, selected }) => {
  const getAngleIcon = (angle) => {
    const icons = {
      product: Zap,
      service: Users,
      technology: Cog,
      business_model: Target,
      experience: Palette,
      process: Cog,
    };
    return icons[angle] || Zap;
  };

  const getAngleColor = (angle) => {
    const colors = {
      product: "bg-accent-blue text-blue-700",
      service: "bg-accent-green text-green-700",
      technology: "bg-accent-purple text-purple-700",
      business_model: "bg-primary-light text-primary",
      experience: "bg-primary-light text-primary",
      process: "bg-stone-100 text-stone-700",
    };
    return colors[angle] || "bg-stone-100 text-stone-700";
  };

  const AngleIcon = getAngleIcon(data.innovation_angle);

  return (
    <div 
      className={`idea-node ${selected ? 'selected' : ''}`}
      data-testid={`idea-node-${data.id}`}
    >
      {/* Like indicator */}
      {data.isLiked && (
        <div className="absolute -top-2 -left-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center">
          <Heart className="w-3 h-3 text-white fill-white" />
        </div>
      )}
      <div className="idea-node-title">{data.title}</div>
      <div className="idea-node-description">{data.description}</div>
      <div className={`idea-node-badge ${getAngleColor(data.innovation_angle)}`}>
        <AngleIcon className="w-3 h-3 inline mr-1" />
        {data.innovation_angle?.replace("_", " ")}
      </div>
      {selected && (
        <div className="absolute -top-2 -right-2 w-6 h-6 bg-primary rounded-full flex items-center justify-center">
          <Check className="w-4 h-4 text-white" />
        </div>
      )}
    </div>
  );
};

const nodeTypes = {
  ideaNode: IdeaNode,
};

const IdeaMapStep = ({ project, setProject, saveProject, goToStep }) => {
  const { t, language } = useLanguage();
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [selectedIdea, setSelectedIdea] = useState(project?.selected_idea || null);
  const [hasGenerated, setHasGenerated] = useState(false);
  const [likedIdeas, setLikedIdeas] = useState(project?.liked_ideas || []);

  // Initialize from saved ideas
  useEffect(() => {
    if (project?.ideas && project.ideas.length > 0) {
      initializeMap(project.ideas, project.connections || []);
      setHasGenerated(true);
      if (project.liked_ideas) {
        setLikedIdeas(project.liked_ideas);
      }
    }
  }, []);

  const initializeMap = (ideas, connections, currentLikedIdeas = likedIdeas) => {
    // Create nodes from ideas with positions
    const newNodes = ideas.map((idea, index) => ({
      id: idea.id,
      type: "ideaNode",
      position: { 
        x: idea.x || (Math.cos((index / ideas.length) * 2 * Math.PI) * 300),
        y: idea.y || (Math.sin((index / ideas.length) * 2 * Math.PI) * 250)
      },
      data: { 
        ...idea, 
        isLiked: currentLikedIdeas.includes(idea.id)
      },
      selected: selectedIdea?.id === idea.id,
    }));

    // Create edges from connections (skip if node IDs don't exist)
    const nodeIds = new Set(ideas.map(i => i.id));
    const newEdges = connections
      .filter(conn => nodeIds.has(conn.source) && nodeIds.has(conn.target))
      .map((conn, index) => ({
        id: `edge-${index}`,
        source: conn.source,
        target: conn.target,
        label: conn.label,
        type: "smoothstep",
        animated: true,
        markerEnd: { type: MarkerType.ArrowClosed },
        style: { stroke: "#E7E5E4", strokeWidth: 2 },
        labelStyle: { fontSize: 10, fill: "#78716C" },
      }));

    setNodes(newNodes);
    setEdges(newEdges);
  };

  const generateIdeas = async () => {
    if (!project?.problem_statement) {
      toast.error(t('common_error'));
      return;
    }

    setIsGenerating(true);
    try {
      const response = await fetch(`${API}/generate/ideas`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          problem_statement: project.problem_statement,
          language,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        
        // Save to project
        await saveProject({ 
          ideas: data.ideas,
          connections: data.connections || [],
          liked_ideas: [],
          current_step: 2
        }, false);
        
        setProject(prev => ({ 
          ...prev, 
          ideas: data.ideas,
          connections: data.connections || [],
          liked_ideas: []
        }));
        
        setLikedIdeas([]);
        initializeMap(data.ideas, data.connections || [], []);
        setHasGenerated(true);
        setSelectedIdea(null);
        toast.success(t('common_success'));
      } else {
        const error = await response.json();
        toast.error(error.detail || t('common_error'));
      }
    } catch (error) {
      console.error("Error generating ideas:", error);
      toast.error(t('common_error'));
    } finally {
      setIsGenerating(false);
    }
  };

  const handleNodeClick = useCallback((event, node) => {
    const idea = node.data;
    setSelectedIdea(idea);
    
    // Update node selection visually
    setNodes(nds => 
      nds.map(n => ({
        ...n,
        selected: n.id === node.id,
        data: { ...n.data },
      }))
    );
  }, [setNodes]);

  // Double-click to like/unlike
  const handleNodeDoubleClick = useCallback((event, node) => {
    const ideaId = node.id;
    const newLikedIdeas = likedIdeas.includes(ideaId)
      ? likedIdeas.filter(id => id !== ideaId)
      : [...likedIdeas, ideaId];
    
    setLikedIdeas(newLikedIdeas);
    
    // Update node data
    setNodes(nds => 
      nds.map(n => ({
        ...n,
        data: { 
          ...n.data, 
          isLiked: newLikedIdeas.includes(n.id)
        },
      }))
    );
    
    // Save likes to project
    saveProject({ liked_ideas: newLikedIdeas }, false);
  }, [likedIdeas, setNodes, saveProject]);

  const handleSelectIdea = async () => {
    if (!selectedIdea) return;

    await saveProject({ 
      selected_idea: selectedIdea,
      liked_ideas: likedIdeas,
      current_step: 2
    });
    
    setProject(prev => ({ ...prev, selected_idea: selectedIdea, liked_ideas: likedIdeas }));
    toast.success(t('common_success'));
    goToStep(3); // Go to Concept step (new order)
  };


  return (
    <ReactFlowProvider>
      <div className="h-full flex flex-col" style={{ height: 'calc(100vh - 180px)' }}>
      {/* Header */}
      <div className="bg-white border-b border-stone-200 px-4 sm:px-8 py-4">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h2 className="font-heading text-2xl font-semibold text-stone-900">
              {t('ideamap_title')}
            </h2>
            <p className="text-stone-600">
              {t('ideamap_description')}
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <div className="flex items-center gap-3">
              <div className="flex flex-col items-end">
                <Button
                  onClick={generateIdeas}
                  disabled={isGenerating}
                  variant={hasGenerated ? "outline" : "default"}
                  className={hasGenerated ? "btn-secondary" : "btn-primary"}
                  data-testid="generate-ideas-btn"
                >
                  {isGenerating ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      {t('ideamap_generating')}
                    </>
                  ) : hasGenerated ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      {t('ideamap_regenerate')}
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      {t('ideamap_generate')}
                    </>
                  )}
                </Button>
                {hasGenerated && (
                  <span className="text-xs text-stone-500 mt-1">
                    {t('ideamap_regenerate_hint')}
                  </span>
                )}
              </div>
            </div>
            {selectedIdea && (
              <Button
                onClick={handleSelectIdea}
                className="btn-primary"
                data-testid="select-idea-btn"
              >
                <Check className="w-4 h-4 mr-2" />
                {t('ideamap_continue')}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Like instruction */}
      {hasGenerated && (
        <div className="bg-primary-light px-4 py-2 text-center text-sm text-primary">
          <Heart className="w-4 h-4 inline mr-2" />
          {t('ideamap_favorite_hint')}
          {likedIdeas.length > 0 && (
            <span className="ml-2 font-medium">
              ({likedIdeas.length} {t('ideamap_likes')})
            </span>
          )}
        </div>
      )}

      {/* Map Area */}
      <div className="flex-1 relative" style={{ height: '100%', minHeight: '400px' }}>
        {!hasGenerated ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <div className="w-20 h-20 bg-primary-light rounded-full flex items-center justify-center mx-auto mb-6">
                <Sparkles className="w-10 h-10 text-primary" />
              </div>
              <h3 className="font-heading text-xl font-semibold text-stone-900 mb-2">
                {t('ideamap_ready_title')}
              </h3>
              <p className="text-stone-600 mb-6 max-w-md">
                {t('ideamap_ready_desc')}
              </p>
              <Button
                onClick={generateIdeas}
                disabled={isGenerating}
                className="btn-primary"
                data-testid="generate-ideas-empty-btn"
              >
                {isGenerating ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    {t('ideamap_generating')}
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    {t('ideamap_generate')}
                  </>
                )}
              </Button>
            </div>
          </div>
        ) : (
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onNodeClick={handleNodeClick}
            onNodeDoubleClick={handleNodeDoubleClick}
            nodeTypes={nodeTypes}
            fitView
            fitViewOptions={{ padding: 0.3 }}
          >
            <Background color="#E7E5E4" gap={20} />
            <Controls className="bg-white" />
          </ReactFlow>
        )}
      </div>

      {/* Selected Idea Panel */}
      {selectedIdea && (
        <div
          className="fixed bottom-24 left-4 right-4 sm:left-auto sm:right-8 sm:w-96 z-50 pointer-events-none"
        >
          <Card className="bg-white border-primary shadow-floating p-4 pointer-events-auto" data-testid="selected-idea-card">
            <div className="flex items-start justify-between mb-2">
              <h4 className="font-heading font-semibold text-stone-900">
                {t('ideamap_selected')}
              </h4>
              <div className="flex items-center gap-2">
                {likedIdeas.includes(selectedIdea.id) && (
                  <Heart className="w-4 h-4 text-red-500 fill-red-500" />
                )}
                <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                  <Check className="w-4 h-4 text-white" />
                </div>
              </div>
            </div>
            <h5 className="font-medium text-primary mb-1">{selectedIdea.title}</h5>
            <p className="text-sm text-stone-600 mb-3">{selectedIdea.description}</p>
            <div className="text-xs bg-primary-light text-primary px-2 py-1 rounded-full inline-block">
              {selectedIdea.innovation_angle?.replace("_", " ")}
            </div>
          </Card>
        </div>
      )}
      </div>
    </ReactFlowProvider>
  );
};

export default IdeaMapStep;
